/**
 * Technique Detection Badge
 * Shows detected Lumo technique with execution score
 */

import React from 'react';
import { cn } from '../utils/cn';
import Icon from './AppIcon';

const TECHNIQUE_ICONS = {
  'mirror-pivot': 'ArrowLeftRight',
  'shrug-close': 'Hand',
  'two-minute-drill': 'Zap',
  'cascade-challenge': 'Flame',
  'human-reset': 'RotateCcw',
  'frame-flip': 'Lightbulb',
  'exit-ramp': 'ArrowRightFromLine'
};

const TechniqueDetectionBadge = ({ 
  detection, 
  executionScore = null,
  techniqueName = null,
  className = '' 
}) => {
  // If no technique detected
  if (!detection?.techniqueSlug) {
    return (
      <div className={cn(
        'p-4 bg-gray-50 border-2 border-gray-200 rounded-xl',
        className
      )}>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gray-100 rounded-lg">
            <Icon name="HelpCircle" className="w-5 h-5 text-gray-400" />
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-700">No Technique Detected</p>
            <p className="text-xs text-gray-500">Try using a branded Lumo technique next time</p>
          </div>
        </div>
      </div>
    );
  }

  // Technique detected
  const techniqueIcon = TECHNIQUE_ICONS[detection.techniqueSlug] || 'Star';
  const confidencePercent = Math.round((detection.confidence || 0) * 100);
  
  // Determine score color
  let scoreColor = 'text-gray-600';
  let scoreBgColor = 'bg-gray-100';
  if (executionScore !== null) {
    if (executionScore >= 0.8) {
      scoreColor = 'text-green-700';
      scoreBgColor = 'bg-green-100';
    } else if (executionScore >= 0.7) {
      scoreColor = 'text-blue-700';
      scoreBgColor = 'bg-blue-100';
    } else if (executionScore >= 0.5) {
      scoreColor = 'text-yellow-700';
      scoreBgColor = 'bg-yellow-100';
    } else {
      scoreColor = 'text-red-700';
      scoreBgColor = 'bg-red-100';
    }
  }

  return (
    <div className={cn(
      'p-4 bg-gradient-to-r from-primary-50 to-secondary-50 border-2 border-primary-200 rounded-xl',
      className
    )}>
      <div className="flex items-start justify-between">
        {/* Left: Technique info */}
        <div className="flex items-start gap-3 flex-1">
          <div className="p-2 bg-primary-100 rounded-lg">
            <Icon name={techniqueIcon} className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <p className="text-sm font-bold text-gray-900">
                {techniqueName || detection.techniqueSlug.split('-').map(w => 
                  w.charAt(0).toUpperCase() + w.slice(1)
                ).join(' ')}
              </p>
              <span className="px-2 py-0.5 bg-primary-600 text-white text-xs rounded-full font-semibold">
                Detected
              </span>
            </div>
            <p className="text-xs text-gray-600 mb-2">{detection.evidence}</p>
            
            {/* Confidence bar */}
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-gray-200 rounded-full h-1.5 max-w-[120px]">
                <div 
                  className="bg-primary-500 h-1.5 rounded-full transition-all"
                  style={{ width: `${confidencePercent}%` }}
                />
              </div>
              <span className="text-xs font-semibold text-gray-600">
                {confidencePercent}% confident
              </span>
            </div>
          </div>
        </div>

        {/* Right: Execution score */}
        {executionScore !== null && (
          <div className={cn(
            'px-4 py-2 rounded-xl border-2',
            scoreBgColor,
            scoreColor.replace('text-', 'border-').replace('-700', '-300')
          )}>
            <p className="text-xs font-semibold text-center mb-1">Execution</p>
            <p className={cn('text-2xl font-bold text-center', scoreColor)}>
              {Math.round(executionScore * 100)}
            </p>
          </div>
        )}
      </div>

      {/* Execution feedback */}
      {executionScore !== null && (
        <div className="mt-3 pt-3 border-t border-primary-200">
          {executionScore >= 0.8 ? (
            <div className="flex items-start gap-2">
              <Icon name="Star" className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-gray-700">
                <strong>Excellent execution!</strong> You applied this technique with confidence and precision.
              </p>
            </div>
          ) : executionScore >= 0.7 ? (
            <div className="flex items-start gap-2">
              <Icon name="CheckCircle" className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-gray-700">
                <strong>Good work!</strong> You executed this technique well. Keep practicing for mastery.
              </p>
            </div>
          ) : executionScore >= 0.5 ? (
            <div className="flex items-start gap-2">
              <Icon name="AlertCircle" className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-gray-700">
                <strong>Getting there.</strong> You used the technique but it needs refinement. Review the steps.
              </p>
            </div>
          ) : (
            <div className="flex items-start gap-2">
              <Icon name="XCircle" className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-gray-700">
                <strong>Needs work.</strong> The technique wasn't executed properly. Study the example and try again.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TechniqueDetectionBadge;
